import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import EuroOutlinedIcon from '@mui/icons-material/EuroOutlined';
import HomeRepairServiceOutlinedIcon from '@mui/icons-material/HomeRepairServiceOutlined';
import LocalPhoneOutlinedIcon from '@mui/icons-material/LocalPhoneOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';

import FirebaseImage from '../../../Components/Media/FirebaseImage';
import DemandLocationPanel from '../../../Components/Geo/DemandLocationPanel';
import RoutePlannerButton from '../../../Components/Geo/RoutePlannerButton';
import ProjectImageCarousel from '../../../Components/Media/ProjectImageCarousel';
import Loader from '../../../Components/UI/Loader/Loader';
import { getDemandsForArtisanAPI } from '../../../Store/Actions/demandActions';
import { confirmVisitByArtisan, markAssessmentReadyByArtisan, resolveCompletionIssueArtisan, savePortfolioProject } from '../../../Store/Actions/actionOffer';
import {
  getClientName,
  getProjectDescription,
  getProjectImages,
} from '../../../Utils/legacyData';
import { getPortfolioStatus, getPortfolioTitle } from '../../../Utils/portfolio';
import PortfolioProjectEditor from '../../Boutique/PortfolioProjectEditor';
import ContractModal from '../Offers/ContractModal';
import ModificationModal from '../Offers/ModificationModal';
import JobFinalisingModal from '../Offers/JobFinalisingModal';
import DeleteOfferArtisan from '../Offers/DeleteOfferArtisan';
import ModalDevisPreview from '../../ClientProjects/ModalDevisPreview';
import SeeReviewArtisan from '../Offers/SeeReviewArtisan';
import ReviewClientByArtisanModal from '../Offers/ReviewClientByArtisanModal';
import ArtisanOffersPanel from '../Offers/ArtisanOffersPanel';
import ProjectConversationPanel from '../../../Components/Project/ProjectConversationPanel';
import ProjectPulse from '../../../Components/Project/ProjectPulse';
import { useI18n } from '../../../i18n/I18nProvider';
import { createOfficialInvoiceDraft, issueOfficialInvoice } from '../../../Services/invoicing';
import ProjectInvoiceModal from '../../../Components/Invoicing/ProjectInvoiceModal';
import { toast } from 'react-toastify';

const TYPE_KEYS = {
  window: 'window',
  electricity: 'electricity',
  plumbing: 'plumbing',
  painting: 'painting',
  masonry: 'masonry',
  roofing: 'roofing',
  heating: 'heating',
  renovation: 'renovation',
};

const typeLabelFor = (type, t) => t(`specialities.${TYPE_KEYS[String(type || '').toLowerCase()] || 'general'}`);

const buildStatusMeta = (t) => ({
  estimation_wait: {
    label: t('jobs.status.estimation_wait.label'), tone: 'pending', description: t('jobs.status.estimation_wait.description'), step: 1,
  },
  estimation_confirmed: {
    label: t('jobs.status.estimation_confirmed.label'), tone: 'info', description: t('jobs.status.estimation_confirmed.description'), step: 2,
  },
  estimation_visit_valid: {
    label: t('jobs.status.estimation_visit_valid.label'), tone: 'action', description: t('jobs.status.estimation_visit_valid.description'), step: 3,
  },
  contract_offered: {
    label: t('jobs.status.contract_offered.label'), tone: 'pending', description: t('jobs.status.contract_offered.description'), step: 4,
  },
  modification_asked: {
    label: t('jobs.status.modification_asked.label'), tone: 'warning', description: t('jobs.status.modification_asked.description'), step: 4,
  },
  assigned: {
    label: t('jobs.status.assigned.label'), tone: 'success', description: t('jobs.status.assigned.description'), step: 5,
  },
  completion_pending: {
    label: t('jobs.status.completion_pending.label'), tone: 'pending', description: t('jobs.status.completion_pending.description'), step: 6,
  },
  completion_disputed: {
    label: t('jobs.status.completion_disputed.label'), tone: 'warning', description: t('jobs.status.completion_disputed.description'), step: 6,
  },
  finished: {
    label: t('jobs.status.finished.label'), tone: 'success', description: t('jobs.status.finished.description'), step: 6,
  },
  paid: {
    label: t('jobs.status.paid.label'), tone: 'success', description: t('jobs.status.paid.description'), step: 7,
  },
  reviewed: {
    label: t('jobs.status.reviewed.label'), tone: 'complete', description: t('jobs.status.reviewed.description'), step: 8,
  },
});

const buildTabs = (t) => ([
  {
    id: 'offers',
    label: t('jobs.tabOffers'),
    statuses: [],
    description: t('jobs.tabOffersDescription'),
  },
  {
    id: 'action',
    label: t('jobs.tabAction'),
    statuses: ['estimation_confirmed', 'estimation_visit_valid', 'modification_asked', 'completion_disputed'],
    description: t('jobs.tabActionDescription'),
  },
  {
    id: 'active',
    label: t('jobs.tabActive'),
    statuses: ['estimation_wait', 'contract_offered', 'assigned', 'completion_pending'],
    description: t('jobs.tabActiveDescription'),
  },
  {
    id: 'finalize',
    label: t('jobs.tabFinalize'),
    statuses: ['finished', 'paid'],
    description: t('jobs.tabFinalizeDescription'),
  },
  {
    id: 'completed',
    label: t('jobs.tabCompleted'),
    statuses: ['reviewed'],
    description: t('jobs.tabCompletedDescription'),
  },
]);

const WITHDRAWABLE_STATUSES = new Set([
  'estimation_wait', 'estimation_confirmed', 'estimation_visit_valid', 'contract_offered', 'modification_asked',
]);

const PAGE_SIZE = 6;

const timeValue = (value) => {
  if (!value) return 0;
  if (typeof value?.toMillis === 'function') return value.toMillis();
  if (typeof value?.seconds === 'number') return value.seconds * 1000;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

const priorityFor = (status) => ({
  modification_asked: 1,
  estimation_visit_valid: 2,
  estimation_confirmed: 3,
  completion_disputed: 1,
  completion_pending: 4,
  finished: 5,
  paid: 6,
}[status] || 20);

const MainJobSection = ({
  artisanId,
  loading,
  error,
  userDemands = [],
  getDemands,
  savePortfolio,
  confirmVisit,
  markAssessmentReady,
  resolveCompletionIssue,
}) => {
  const history = useHistory();
  const { language, locale, t, formatCurrency, formatDate } = useI18n();
  const en = language === 'en';
  const statusMeta = useMemo(() => buildStatusMeta(t), [t]);
  const tabs = useMemo(() => buildTabs(t), [t]);
  const [selectedDemand, setSelectedDemand] = useState(null);
  const [activeModal, setActiveModal] = useState('');
  const [activeTab, setActiveTab] = useState('active');
  const [tabInitialized, setTabInitialized] = useState(false);
  const [query, setQuery] = useState('');
  const [sortBy, setSortBy] = useState('priority');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [offerCounts, setOfferCounts] = useState({ pending: 0, accepted: 0, declined: 0, total: 0 });
  const [invoiceBusy, setInvoiceBusy] = useState('');
  const [invoiceViewerDemandId, setInvoiceViewerDemandId] = useState('');

  useEffect(() => {
    const unsubscribe = getDemands();
    return typeof unsubscribe === 'function' ? unsubscribe : undefined;
  }, [getDemands]);

  const projects = useMemo(
    () => userDemands.filter(
      (demand) => demand?.estimation?.artisan_id === artisanId && statusMeta[demand?.demand_status],
    ),
    [artisanId, statusMeta, userDemands],
  );

  const counts = useMemo(() => tabs.reduce((result, tab) => ({
    ...result,
    [tab.id]: tab.id === 'offers'
      ? offerCounts.pending
      : projects.filter((project) => tab.statuses.includes(project.demand_status)).length,
  }), {}), [offerCounts.pending, projects, tabs]);

  const handleOfferCountsChange = useCallback((nextCounts) => {
    setOfferCounts((current) => (
      current.pending === nextCounts.pending
      && current.accepted === nextCounts.accepted
      && current.declined === nextCounts.declined
      && current.total === nextCounts.total
        ? current
        : nextCounts
    ));
  }, []);

  useEffect(() => {
    if (tabInitialized || (projects.length === 0 && offerCounts.total === 0)) return;
    if (counts.action > 0) setActiveTab('action');
    else if (counts.offers > 0) setActiveTab('offers');
    else if (counts.active > 0) setActiveTab('active');
    else if (counts.finalize > 0) setActiveTab('finalize');
    else setActiveTab('completed');
    setTabInitialized(true);
  }, [counts.action, counts.active, counts.finalize, counts.offers, offerCounts.total, projects.length, tabInitialized]);

  useEffect(() => setVisibleCount(PAGE_SIZE), [activeTab, query, sortBy]);

  const selectedTab = tabs.find((tab) => tab.id === activeTab) || tabs[1];

  const filteredProjects = useMemo(() => {
    const normalizedQuery = query.trim().toLocaleLowerCase(locale);
    if (selectedTab.id === 'offers') return [];
    const result = projects.filter((demand) => {
      if (!selectedTab.statuses.includes(demand.demand_status)) return false;
      if (!normalizedQuery) return true;
      const haystack = [
        getClientName(demand),
        getProjectDescription(demand),
        demand.demand_address,
        demand.demand_type,
        demand.contract?.total_cost,
      ].filter(Boolean).join(' ').toLocaleLowerCase(locale);
      return haystack.includes(normalizedQuery);
    });

    return result.sort((a, b) => {
      if (sortBy === 'date-oldest') return timeValue(a.date) - timeValue(b.date);
      if (sortBy === 'amount') return Number(b.contract?.total_cost || 0) - Number(a.contract?.total_cost || 0);
      if (sortBy === 'priority') {
        const delta = priorityFor(a.demand_status) - priorityFor(b.demand_status);
        if (delta !== 0) return delta;
      }
      return timeValue(b.updated_at || b.date) - timeValue(a.updated_at || a.date);
    });
  }, [locale, projects, query, selectedTab, sortBy]);

  const displayedProjects = filteredProjects.slice(0, visibleCount);

  const openModal = (name, demand) => {
    setSelectedDemand(demand);
    setActiveModal(name);
  };

  const closeModal = () => {
    setActiveModal('');
    setSelectedDemand(null);
  };

  const handlePortfolioSave = (values) => savePortfolio({
    demandId: selectedDemand?.demand_id,
    artisanId,
    ...values,
  });

  const issueProjectInvoice = async (demand) => {
    if (!window.confirm(en
      ? 'Issue and transmit the final invoice? It cannot be edited afterward.'
      : 'Émettre et transmettre la facture finale ? Elle ne pourra plus être modifiée ensuite.')) return;
    setInvoiceBusy(demand.demand_id);
    try {
      const draft = await createOfficialInvoiceDraft(demand.demand_id);
      const invoiceId = draft.invoiceId || demand.invoicing?.invoice_id;
      if (!invoiceId) throw new Error(en ? 'Unable to prepare the invoice.' : 'Impossible de préparer la facture.');
      if (!draft.status || draft.status === 'draft') await issueOfficialInvoice(demand.demand_id);
      toast.success(en ? 'Final invoice transmitted.' : 'Facture finale transmise.');
      setInvoiceViewerDemandId(demand.demand_id);
      getDemands();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setInvoiceBusy('');
    }
  };

  return (
    <Section>
      {loading && projects.length === 0 && <Loader />}
      {error && <ErrorMessage>{error}</ErrorMessage>}

      <ListHeader>
        <div>
          <Eyebrow>{t('jobs.workspaceEyebrow')}</Eyebrow><ListTitle>{t('jobs.workspaceTitle')}</ListTitle><ListSubtitle>{t('jobs.workspaceSubtitle')}</ListSubtitle>
        </div>
        <HeaderTools>
          <Summary aria-label={t('jobs.summaryAria', { projects: projects.length, offers: offerCounts.pending })}>
            <SummaryItem $highlight={offerCounts.pending > 0}>
              <strong>{offerCounts.pending || 0}</strong><span>{t('jobs.summaryOffers')}</span>
            </SummaryItem>
            <SummaryItem $highlight={counts.action > 0}>
              <strong>{counts.action || 0}</strong><span>{t('jobs.summaryActions')}</span>
            </SummaryItem>
            <SummaryItem>
              <strong>{counts.active || 0}</strong><span>{t('jobs.summaryActive')}</span>
            </SummaryItem>
            <SummaryItem>
              <strong>{counts.finalize || 0}</strong><span>{t('jobs.summaryFinalize')}</span>
            </SummaryItem>
          </Summary>
          <RoutePlannerButton projects={projects} />
        </HeaderTools>
      </ListHeader>

      <Tabs role="tablist" aria-label={t('jobs.tabsAria')}>
        {tabs.map((tab) => (
          <Tab
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.id}
            $active={activeTab === tab.id}
            $attention={tab.id === 'action' && counts.action > 0}
            onClick={() => setActiveTab(tab.id)}
          >
            <span>{tab.label}</span>
            <strong>{counts[tab.id] || 0}</strong>
          </Tab>
        ))}
      </Tabs>

      {activeTab !== 'offers' && (
        <Toolbar>
          <ToolbarTop>
            <TabIntro>
              <strong>{selectedTab.label}</strong>
              {(query || sortBy !== 'priority') && <span>{en ? 'Filters applied' : 'Filtres appliqués'}</span>}
            </TabIntro>
            <MobileFilterButton type="button" onClick={() => setFiltersOpen((value) => !value)} aria-expanded={filtersOpen}>
              <TuneOutlinedIcon /> {filtersOpen ? (en ? 'Hide filters' : 'Masquer') : (en ? 'Filters' : 'Filtres')}
            </MobileFilterButton>
          </ToolbarTop>
          <Tools $open={filtersOpen}>
            <SearchBox>
              <SearchOutlinedIcon />
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={t('jobs.searchPlaceholder')}
                aria-label={t('jobs.searchAria')}
              />
            </SearchBox>
            <SortSelect value={sortBy} onChange={(event) => setSortBy(event.target.value)} aria-label={t('jobs.sortAria')}>
              <option value="priority">{t('jobs.sortPriority')}</option>
              <option value="date-newest">{t('jobs.sortNewest')}</option>
              <option value="date-oldest">{t('jobs.sortOldest')}</option>
              <option value="amount">{t('jobs.sortAmount')}</option>
            </SortSelect>
          </Tools>
        </Toolbar>
      )}

      <OffersMount $visible={activeTab === 'offers'}>
        <ArtisanOffersPanel
          artisanId={artisanId}
          onCountsChange={handleOfferCountsChange}
        />
      </OffersMount>

      {activeTab !== 'offers' && (!loading && filteredProjects.length === 0 ? (
        <EmptyState>
          {activeTab === 'completed' ? <ArchiveOutlinedIcon /> : <HomeRepairServiceOutlinedIcon />}
          <h3>{query ? t('jobs.noResults') : t('jobs.noTabJobs', { tab: selectedTab.label })}</h3>
          <p>{query ? t('jobs.tryAnotherKeyword') : selectedTab.description}</p>
        </EmptyState>
      ) : activeTab === 'completed' ? (
        <ArchiveList>
          {displayedProjects.map((demand) => {
            const images = getProjectImages(demand);
            const clientName = getClientName(demand);
            const portfolioStatus = getPortfolioStatus(demand);
            const title = getPortfolioTitle(demand);
            return (
              <ArchiveCard key={demand.demand_id}>
                <ArchiveMedia>
                  <ArchiveImage
                    src={images[0]}
                    fallbackSrc="/images/project-placeholder.svg"
                    alt={t('jobs.archiveAlt', { title })}
                  />
                </ArchiveMedia>
                <ArchiveBody>
                  <ArchiveTop>
                    <div>
                      <ArchiveLabel>{t('jobs.completedProject')} · {formatDate(demand.date)}</ArchiveLabel>
                      <h3>{title}</h3>
                      <p>{clientName} · {typeLabelFor(demand.demand_type, t)}</p>
                    </div>
                    <PortfolioBadge $status={portfolioStatus}>
                      {portfolioStatus === 'published' ? t('jobs.portfolioPublished')
                        : portfolioStatus === 'draft' ? t('jobs.portfolioDraft')
                          : portfolioStatus === 'hidden' ? t('jobs.portfolioHidden') : t('jobs.portfolioMissing')}
                    </PortfolioBadge>
                  </ArchiveTop>
                  <ArchiveActions>
                    <PrimaryButton type="button" onClick={() => openModal('portfolio', demand)}>
                      <StorefrontOutlinedIcon />
                      {portfolioStatus === 'available' ? t('jobs.portfolioAdd') : t('jobs.portfolioEdit')}
                    </PrimaryButton>
                    <SecondaryButton type="button" onClick={() => openModal('review', demand)}>
                      <RateReviewOutlinedIcon /> {t('jobs.viewReview')}
                    </SecondaryButton>
                    {demand.contract && (
                      <SecondaryButton type="button" onClick={() => openModal('preview', demand)}>
                        <VisibilityOutlinedIcon /> {t('jobs.viewFile')}
                      </SecondaryButton>
                    )}
                    {demand.invoicing?.invoice_id ? (
                      <SecondaryButton type="button" onClick={() => setInvoiceViewerDemandId(demand.demand_id)}>
                        <ReceiptLongOutlinedIcon /> {en ? 'View invoice' : 'Voir la facture'}
                      </SecondaryButton>
                    ) : (
                      <PrimaryButton
                        type="button"
                        onClick={() => issueProjectInvoice(demand)}
                        disabled={invoiceBusy === demand.demand_id}
                      >
                        <ReceiptLongOutlinedIcon /> {invoiceBusy === demand.demand_id
                          ? (en ? 'Preparing…' : 'Préparation…')
                          : (en ? 'Issue final invoice' : 'Émettre la facture finale')}
                      </PrimaryButton>
                    )}
                  </ArchiveActions>
                </ArchiveBody>
              </ArchiveCard>
            );
          })}
        </ArchiveList>
      ) : (
        <Cards>
          {displayedProjects.map((demand) => {
            const status = statusMeta[demand.demand_status];
            const images = getProjectImages(demand);
            const description = getProjectDescription(demand);
            const clientName = getClientName(demand);
            const hasContract = Boolean(demand.contract);
            const canWithdraw = WITHDRAWABLE_STATUSES.has(demand.demand_status);
            const visitDate = demand.estimation?.visit_date;
            const phone = demand.client_phone_number
              || demand.estimation?.client_phone_number
              || demand.contract?.client_phone_number;
            const typeLabel = typeLabelFor(demand.demand_type, t);
            const requiresAction = ['estimation_confirmed', 'estimation_visit_valid', 'modification_asked', 'completion_disputed'].includes(demand.demand_status);
            const assessmentMode = demand?.assessment_workflow?.mode || demand?.estimation?.assessment_mode || 'visit_required';
            const artisanVisitConfirmed = Boolean(demand?.visit_confirmation?.artisan_confirmed_at);
            const depositPaid = demand?.payments?.deposit?.status === 'paid' || Boolean(demand?.deposit_paid_at);

            return (
              <ProjectCard key={demand.demand_id} $attention={requiresAction}>
                {requiresAction && <AttentionBanner>{t('jobs.attention')} · {status.description}</AttentionBanner>}
                <MissionCarousel images={images} fallbackSrc="/images/project-placeholder.svg" alt={t('jobs.photoAlt', { type: typeLabel })} />

                <CardBody>
                  <CardTop>
                    <CategoryBadge>{typeLabel}</CategoryBadge>
                    <StatusBadge $tone={status.tone}>{status.label}</StatusBadge>
                  </CardTop>

                  <ClientRow type="button" onClick={() => history.push('/clientprofile', { clientId: demand.user_id, fromDemandId: demand.demand_id })}>
                    <ClientAvatar src={demand.client_profile_image} fallbackSrc="/images/user.svg" alt={t('profileSidebar.profileOf', { name: clientName })} />
                    <div><span>{t('jobs.projectBy')} · {t('jobs.viewClientProfile')}</span><strong>{clientName}</strong><ClientTrust>{demand.client_average_rating == null ? t('jobs.viewScore') : (Number(demand.client_average_rating) > 0 ? `★ ${Number(demand.client_average_rating).toFixed(1)}` : t('common.new'))} · {demand.client_verified_projects == null ? t('jobs.clientProfile') : t('jobs.verifiedProjects', { count: Number(demand.client_verified_projects || 0) })}</ClientTrust></div>
                  </ClientRow>

                  <Description>{description || t('jobs.noDescription')}</Description>

                  <ProjectPulse demand={demand} role="Artisan" />

                  <Details>
                    <Detail><CalendarTodayOutlinedIcon /><div><span>{t('jobs.published')}</span><strong>{formatDate(demand.date)}</strong></div></Detail>
                    {visitDate && <Detail><TaskAltOutlinedIcon /><div><span>{t('jobs.visitPlanned')}</span><strong>{formatDate(visitDate)}</strong></div></Detail>}
                    {hasContract && <Detail><EuroOutlinedIcon /><div><span>{t('jobs.quoteAmount')}</span><strong>{formatCurrency(demand.contract?.total_cost)}</strong></div></Detail>}
                  </Details>

                  <DemandLocationPanel
                    demand={demand}
                    allowNavigation={status.step >= 2}
                    allowArrivalConfirmation={demand.demand_status === 'assigned' && depositPaid}
                  />

                  {phone && status.step >= 2 && (
                    <ContactCard><LocalPhoneOutlinedIcon /><div><span>{t('jobs.clientContact')}</span><a href={`tel:${phone}`}>{phone}</a></div></ContactCard>
                  )}

                  {!requiresAction && (
                    <StatusPanel $tone={status.tone}><CheckCircleOutlineIcon /><div><strong>{status.label}</strong><p>{status.description}</p></div></StatusPanel>
                  )}

                  {demand.demand_status === 'estimation_confirmed' && assessmentMode === 'visit_required' && (
                    <WorkflowNotice>
                      {artisanVisitConfirmed
                        ? t('jobs.visitConfirmedWaitingClient')
                        : t('jobs.visitConfirmInstruction')}
                    </WorkflowNotice>
                  )}
                  {demand.demand_status === 'estimation_confirmed' && assessmentMode === 'more_info' && (
                    <WorkflowNotice>{t('jobs.moreInfoInstruction')}</WorkflowNotice>
                  )}
                  {demand.demand_status === 'completion_disputed' && (
                    <WorkflowNotice $warning>{t('jobs.issuePrefix')} {demand?.completion?.issue_reason || t('jobs.issueFallback')}</WorkflowNotice>
                  )}
                  {demand.demand_status === 'assigned' && !depositPaid && (
                    <WorkflowNotice $warning>Attendez la confirmation de l’acompte avant de vous déplacer ou de commencer le chantier.</WorkflowNotice>
                  )}

                  <ProjectConversationPanel demand={demand} />

                  <Actions>
                    {demand.demand_status === 'estimation_confirmed' && assessmentMode === 'visit_required' && !artisanVisitConfirmed && <PrimaryButton type="button" onClick={() => confirmVisit(demand)}><TaskAltOutlinedIcon /> {t('jobs.confirmVisit')}</PrimaryButton>}
                    {demand.demand_status === 'estimation_confirmed' && assessmentMode === 'more_info' && <PrimaryButton type="button" onClick={() => markAssessmentReady(demand)}><DescriptionOutlinedIcon /> {t('jobs.infoReady')}</PrimaryButton>}
                    {demand.demand_status === 'estimation_visit_valid' && <PrimaryButton type="button" onClick={() => openModal('contract', demand)}><DescriptionOutlinedIcon /> {t('jobs.prepareQuote')}</PrimaryButton>}
                    {demand.demand_status === 'modification_asked' && <PrimaryButton type="button" onClick={() => openModal('modification', demand)}><TuneOutlinedIcon /> {t('jobs.reviseQuote')}</PrimaryButton>}
                    {demand.demand_status === 'completion_disputed' && <PrimaryButton type="button" onClick={() => resolveCompletionIssue(demand)}><TaskAltOutlinedIcon /> {t('jobs.issueResolved')}</PrimaryButton>}
                    {demand.demand_status === 'assigned' && depositPaid && <PrimaryButton type="button" onClick={() => openModal('finish', demand)}><TaskAltOutlinedIcon /> {t('jobs.declareFinished')}</PrimaryButton>}
                    {demand.demand_status === 'paid' && <PrimaryButton type="button" onClick={() => openModal('client-review', demand)}><RateReviewOutlinedIcon /> {t('jobs.reviewClient')}</PrimaryButton>}
                    {hasContract && <SecondaryButton type="button" onClick={() => openModal('preview', demand)}><VisibilityOutlinedIcon /> {t('jobs.viewQuote')}</SecondaryButton>}
                    {canWithdraw && <DangerButton type="button" onClick={() => openModal('delete', demand)}><DeleteOutlineOutlinedIcon /> {t('jobs.withdrawOffer')}</DangerButton>}
                  </Actions>
                </CardBody>
              </ProjectCard>
            );
          })}
        </Cards>
      ))}

      {activeTab !== 'offers' && filteredProjects.length > visibleCount && (
        <LoadMore type="button" onClick={() => setVisibleCount((count) => count + PAGE_SIZE)}>
          {t('jobs.showMore', { count: Math.min(PAGE_SIZE, filteredProjects.length - visibleCount) })}
        </LoadMore>
      )}

      {selectedDemand && (
        <>
          <ContractModal showModal={activeModal === 'contract' ? 'open' : 'close'} handleClick={closeModal} demand={selectedDemand} />
          <ModificationModal showModal={activeModal === 'modification' ? 'open' : 'close'} handleClick={closeModal} demand={selectedDemand} />
          <JobFinalisingModal showModal={activeModal === 'finish' ? 'open' : 'close'} handleClick={closeModal} demand={selectedDemand} />
          <ModalDevisPreview showModal={activeModal === 'preview' ? 'open' : 'close'} handleClick={closeModal} demand={selectedDemand} />
          <DeleteOfferArtisan show={activeModal === 'delete'} close={closeModal} demand={selectedDemand} />
          <SeeReviewArtisan show={activeModal === 'review'} close={closeModal} demand={selectedDemand} />
          <ReviewClientByArtisanModal show={activeModal === 'client-review'} close={closeModal} demand={selectedDemand} />
          <PortfolioProjectEditor
            opened={activeModal === 'portfolio'}
            project={selectedDemand}
            saving={loading}
            onClose={closeModal}
            onSave={handlePortfolioSave}
          />
        </>
      )}
      <ProjectInvoiceModal
        demandId={invoiceViewerDemandId}
        open={Boolean(invoiceViewerDemandId)}
        onClose={() => setInvoiceViewerDemandId('')}
      />
    </Section>
  );
};

const Section = styled.section`
  width: 100%;
  min-width: 0;

  /* The artisan mobile nav is fixed to the viewport. Reserve enough room so
     the final archive actions can always scroll completely above it. */
  @media (max-width: 48rem) {
    padding-bottom: calc(8.5rem + env(safe-area-inset-bottom, 0px));
  }
`;
const OffersMount = styled.div`display: ${({ $visible }) => ($visible ? 'block' : 'none')};`;
const ListHeader = styled.header`
  display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 1.1rem; margin-bottom: 1rem; padding: clamp(1.2rem, 2.8vw, 1.7rem); border: 1px solid var(--color-border); border-radius: 1.35rem; background: #fff; box-shadow: var(--shadow-sm);
  & > div:first-child { flex: 1 1 28rem; min-width: 0; }
`;
const Eyebrow = styled.p`margin: 0 0 0.35rem; color: #a88a5d; font-size: 0.72rem; font-weight: 900; letter-spacing: 0.1em; text-transform: uppercase;`;
const ListTitle = styled.h2`margin: 0; color: var(--color-mainDark); font-size: clamp(1.45rem, 3vw, 2rem); line-height: 1.15;`;
const ListSubtitle = styled.p`max-width: 48rem; margin: 0.55rem 0 0; color: var(--color-textMuted); line-height: 1.6;`;
const HeaderTools = styled.div`display: grid; justify-items: end; gap: .7rem; @media (max-width: 38rem) { width: 100%; justify-items: stretch; }`;
const Summary = styled.div`display:grid;grid-template-columns:repeat(4,minmax(5rem,1fr));gap:.5rem;@media(max-width:52rem){display:flex;width:100%;overflow-x:auto;gap:.4rem;padding-bottom:.1rem;scrollbar-width:none;&::-webkit-scrollbar{display:none;}}`;
const SummaryItem = styled.div`
  display: grid; justify-items: center; gap: 0.1rem; padding: 0.75rem; border-radius: 0.9rem; background: ${({ $highlight }) => ($highlight ? '#fff0e5' : '#eef4f8')}; color: ${({ $highlight }) => ($highlight ? '#873d18' : 'var(--color-mainDark)')}; text-align: center;
  strong { font-size: 1.35rem; line-height: 1; } span { color: inherit; font-size: 0.66rem; font-weight: 850; text-transform: uppercase; }
  @media(max-width:52rem){flex:0 0 5.2rem;padding:.58rem .65rem;strong{font-size:1.1rem;}span{font-size:.58rem;}}
`;
const Tabs = styled.div`display: flex; gap: 0.55rem; overflow-x: auto; padding: 0.25rem 0 0.65rem; scrollbar-width: thin;`;
const Tab = styled.button`
  min-width: max-content; display: inline-flex; align-items: center; gap: 0.55rem; padding: 0.7rem 0.85rem; border: 1px solid ${({ $active, $attention }) => ($active ? '#111111' : $attention ? '#efb99f' : '#e7dfd2')}; border-radius: 999px; background: ${({ $active }) => ($active ? '#111111' : '#fff')}; color: ${({ $active }) => ($active ? '#fff' : '#5f5a52')}; font: inherit; font-size: 0.82rem; font-weight: 850; cursor: pointer;
  strong { min-width: 1.35rem; padding: 0.12rem 0.32rem; border-radius: 999px; background: ${({ $active, $attention }) => ($active ? 'rgba(255,255,255,.18)' : $attention ? '#fff0e8' : '#f1ece5')}; text-align: center; }
`;
const Toolbar = styled.div`
  display:grid;grid-template-columns:auto minmax(0,1fr);align-items:center;gap:.8rem;margin-bottom:1rem;padding:.7rem .8rem;
  border:1px solid #e7dfd2;border-radius:1rem;background:rgba(255,255,255,.88);
  @media(max-width:48rem){grid-template-columns:1fr;padding:.55rem .65rem;}
`;
const ToolbarTop = styled.div`display:flex;align-items:center;justify-content:space-between;gap:.6rem;min-width:0;`;
const TabIntro = styled.div`display:grid;gap:.08rem;min-width:0;strong{color:#111111;font-size:.82rem;}span{color:#8a641a;font-size:.65rem;font-weight:800;}`;
const MobileFilterButton = styled.button`
  display:none;align-items:center;justify-content:center;gap:.35rem;min-height:2.45rem;padding:.5rem .68rem;border:1px solid #ded6ca;border-radius:999px;background:#fff;color:#111;font:inherit;font-size:.72rem;font-weight:850;cursor:pointer;
  svg{font-size:.95rem;}@media(max-width:48rem){display:inline-flex;}
`;
const Tools = styled.div`
  display:flex;justify-content:flex-end;gap:.6rem;min-width:0;
  @media(max-width:48rem){display:${({ $open }) => ($open ? 'grid' : 'none')};grid-template-columns:minmax(0,1fr) auto;padding-top:.2rem;}
  @media(max-width:36rem){grid-template-columns:1fr;}
`;
const SearchBox = styled.label`
  min-width: min(20rem, 48vw); display: flex; align-items: center; gap: 0.45rem; padding: 0.58rem 0.72rem; border: 1px solid #e7dfd2; border-radius: 0.75rem; background: #fff; color: #6b7c87;
  svg { font-size: 1.1rem; } input { min-width: 0; width: 100%; border: 0; outline: 0; background: transparent; color: #20394b; font: inherit; }
  @media (max-width: 36rem) { min-width: 0; width: 100%; }
`;
const SortSelect = styled.select`padding: 0.58rem 0.72rem; border: 1px solid #e7dfd2; border-radius: 0.75rem; background: #fff; color: #111111; font: inherit; font-size: 0.82rem; font-weight: 750;`;
const Cards = styled.div`display: grid; gap: 1.6rem;`;
const ProjectCard = styled.article`
  min-width: 0; overflow: hidden; border: 1px solid ${({ $attention }) => ($attention ? '#edc2aa' : 'rgba(0,0,0,.09)')}; border-radius: 1.35rem; background: #fff; box-shadow: 0 1rem 2.8rem rgba(0,0,0,.09);
`;
const AttentionBanner = styled.div`padding: 0.72rem 1rem; background: linear-gradient(90deg, #fff0e5, #fff8f3); color: #7f3e1c; font-size: 0.82rem; font-weight: 850;`;
const MissionCarousel = styled(ProjectImageCarousel)`border-radius: 0;`;
const CardBody = styled.div`min-width: 0; display: grid; gap: 1rem; padding: clamp(1.2rem, 3vw, 1.8rem);`;
const CardTop = styled.div`display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 0.75rem;`;
const CategoryBadge = styled.span`display: inline-flex; align-items: center; min-height: 1.9rem; padding: 0.35rem 0.7rem; border-radius: 999px; background: #edf4fa; color: var(--color-main); font-size: 0.75rem; font-weight: 850;`;
const StatusBadge = styled.span`
  display: inline-flex; align-items: center; min-height: 1.9rem; padding: 0.35rem 0.7rem; border-radius: 999px; font-size: 0.75rem; font-weight: 850; white-space: nowrap;
  color: ${({ $tone }) => ({ warning: '#823b16', success: '#17613f', complete: '#31556c', action: '#64490f', info: '#24547a' }[$tone] || '#6c541c')};
  background: ${({ $tone }) => ({ warning: '#fff0e5', success: '#e9f8f0', complete: '#f3eee6', action: '#fff5d9', info: '#f3eee6' }[$tone] || '#fff7e5')};
`;
const ClientRow = styled.button`
  width:100%;border:0;background:transparent;padding:0;color:inherit;font:inherit;text-align:left;cursor:pointer;
  display: flex; align-items: center; gap: 0.75rem; min-width: 0;
  & > div { display: grid; min-width: 0; } span { color: var(--color-textMuted); font-size: 0.7rem; font-weight: 800; letter-spacing: 0.05em; text-transform: uppercase; } strong { overflow: hidden; color: var(--color-mainDark); font-size: 1.02rem; text-overflow: ellipsis; white-space: nowrap; }
`;
const ClientTrust = styled.small`color:#8a641a;font-size:.62rem;font-weight:800;`;
const ClientAvatar = styled(FirebaseImage)`width: 3.25rem; height: 3.25rem; flex: 0 0 auto; border: 3px solid #fff; border-radius: 50%; object-fit: cover; background: #edf2f4; box-shadow: 0 .35rem 1rem rgba(0,0,0,.14);`;
const Description = styled.p`max-width: 58rem; margin: 0; color: var(--color-text); font-size: 1rem; line-height: 1.7; white-space: pre-wrap;`;
const ProgressHeader = styled.div`display: flex; align-items: center; justify-content: space-between; gap: 1rem; color: var(--color-textMuted); font-size: 0.75rem; strong { color: var(--color-mainDark); }`;
const Progress = styled.div`height: .42rem; overflow: hidden; border-radius: 999px; background: #edf1f4;`;
const ProgressBar = styled.div`width: ${({ $progress }) => `${$progress}%`}; height: 100%; border-radius: inherit; background: linear-gradient(90deg,#d3a13d,#e9c56c); transition: width .25s ease;`;
const Details = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: .75rem;
  width: 100%;
  min-width: 0;

  @media (max-width: 78rem) {
    grid-template-columns: 1fr;
  }
`;
const Detail = styled.div`
  min-width: 0;
  width: 100%;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: start;
  gap: .65rem;
  padding: .85rem;
  overflow: hidden;
  border-radius: .85rem;
  background: #faf8f4;
  color: #335167;

  svg { flex: 0 0 auto; margin-top: .12rem; color: var(--color-main); font-size: 1.08rem; }
  div { display: grid; min-width: 0; }
  span { color: #726b61; font-size: .68rem; font-weight: 800; letter-spacing: .04em; text-transform: uppercase; }
  strong { min-width: 0; overflow-wrap: anywhere; font-size: .86rem; line-height: 1.4; }
`;
const ContactCard = styled.div`
  display: flex; align-items: center; gap: .7rem; padding: .8rem .9rem; border: 1px solid #dbe8ef; border-radius: .9rem; background: #f3f9fc; color: #28536d;
  svg { flex: 0 0 auto; font-size: 1.2rem; } div { display: grid; min-width: 0; } span { color: #71828d; font-size: .68rem; font-weight: 800; letter-spacing: .04em; text-transform: uppercase; } a { color: #214f6d; font-weight: 900; text-decoration: none; }
`;
const StatusPanel = styled.div`
  display: flex; align-items: flex-start; gap: .7rem; padding: .85rem .95rem; border-radius: .9rem; background: ${({ $tone }) => ($tone === 'warning' ? '#fff8f2' : $tone === 'success' || $tone === 'complete' ? '#f1f9f5' : '#fbf8ef')}; color: ${({ $tone }) => ($tone === 'warning' ? '#7a431f' : $tone === 'success' || $tone === 'complete' ? '#245b43' : '#66531f')};
  svg { flex: 0 0 auto; margin-top: .12rem; font-size: 1.1rem; } div { display: grid; gap: .15rem; } strong { font-size: .88rem; } p { margin: 0; font-size: .84rem; line-height: 1.55; }
`;
const WorkflowNotice = styled.p`margin:.85rem 0 0;padding:.8rem;border-radius:.8rem;background:${({$warning})=>$warning?'#fff1ef':'#f5f0e7'};color:${({$warning})=>$warning?'#8c332c':'#6e5a3c'};font-size:.72rem;line-height:1.5;`;
const Actions = styled.div`display: flex; flex-wrap: wrap; gap: .65rem; padding-top: .1rem; @media (max-width: 34rem) { flex-direction: column; }`;
const BaseButton = styled.button`
  min-height: 2.7rem; display: inline-flex; align-items: center; justify-content: center; gap: .45rem; padding: .7rem 1rem; border-radius: .78rem; font: inherit; font-size: .84rem; font-weight: 850; cursor: pointer; transition: transform .18s ease, background .18s ease;
  &:hover { transform: translateY(-1px); } svg { font-size: 1.05rem; } @media (max-width: 34rem) { width: 100%; }
`;
const PrimaryButton = styled(BaseButton)`border: 0; background: var(--color-main); color: #fff; box-shadow: 0 .5rem 1.1rem rgba(0,0,0,.18); &:hover { background: var(--color-mainDark); }`;
const SecondaryButton = styled(BaseButton)`border: 1px solid #e7dfd2; background: #fff; color: #111111; &:hover { background: #faf8f4; }`;
const DangerButton = styled(BaseButton)`border: 1px solid #edd4d1; background: #fff8f7; color: #913b33; &:hover { background: #ffefed; }`;
const ArchiveList = styled.div`
  display: grid;
  gap: .9rem;

  @media (max-width: 42rem) {
    gap: 1.15rem;
  }
`;

const ArchiveCard = styled.article`
  display: grid;
  grid-template-columns: 11rem minmax(0, 1fr);
  min-width: 0;
  overflow: hidden;
  border: 1px solid #e7dfd2;
  border-radius: 1.1rem;
  background: #fff;
  box-shadow: 0 .7rem 1.8rem rgba(0,0,0,.06);

  @media (max-width: 42rem) {
    /* Never allow the old desktop media/content composition to overlap on
       phones. The image and text become two normal document-flow blocks. */
    display: block;
    overflow: hidden;
    border-radius: 1.25rem;
  }
`;

const ArchiveMedia = styled.div`
  position: relative;
  min-width: 0;
  min-height: 10rem;
  overflow: hidden;
  background: #f1ece5;

  @media (max-width: 42rem) {
    width: 100%;
    min-height: 0;
    aspect-ratio: 4 / 3;
    border-bottom: 1px solid #e7dfd2;
  }
`;

const ArchiveImage = styled(FirebaseImage)`
  width: 100% !important;
  height: 100% !important;
  min-height: 10rem;
  display: block !important;
  position: static !important;
  inset: auto !important;
  object-fit: contain;
  padding: .55rem;
  box-sizing: border-box;
  background: #f1ece5;

  @media (max-width: 42rem) {
    min-height: 0;
    padding: 0;
    object-fit: cover;
  }
`;

const ArchiveBody = styled.div`
  position: relative;
  z-index: 2;
  min-width: 0;
  display: grid;
  gap: 1rem;
  padding: 1rem;
  background: #fff;
  color: #111;

  @media (max-width: 42rem) {
    padding: 1rem 1rem 1.15rem;
  }
`;

const ArchiveTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  min-width: 0;

  & > div {
    min-width: 0;
  }

  h3 {
    margin: .3rem 0;
    color: #111;
    font-size: 1.05rem;
    line-height: 1.35;
    overflow-wrap: anywhere;
  }

  p {
    margin: 0;
    color: #667681;
    font-size: .82rem;
    line-height: 1.45;
    overflow-wrap: anywhere;
  }

  @media (max-width: 42rem) {
    flex-direction: column;
    gap: .7rem;
  }
`;

const ArchiveLabel = styled.span`
  display: block;
  color: #71818b;
  font-size: .7rem;
  font-weight: 800;
  line-height: 1.35;
  text-transform: uppercase;
`;

const PortfolioBadge = styled.span`
  flex: 0 0 auto;
  align-self: flex-start;
  max-width: 100%;
  padding: .38rem .62rem;
  border-radius: 999px;
  background: ${({ $status }) => ($status === 'published' ? '#e7f7ee' : $status === 'draft' ? '#fff4d9' : '#f1ece5')};
  color: ${({ $status }) => ($status === 'published' ? '#1f6845' : $status === 'draft' ? '#75551a' : '#5c6e79')};
  font-size: .7rem;
  font-weight: 850;
  white-space: normal;
`;

const ArchiveActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: .55rem;

  @media (max-width: 42rem) {
    display: grid;
    grid-template-columns: 1fr;
    gap: .7rem;

    & > button {
      width: 100%;
      min-height: 3.1rem;
    }
  }
`;
const LoadMore = styled.button`display: block; margin: 1.2rem auto 0; padding: .7rem 1rem; border: 1px solid #d5e0e6; border-radius: 999px; background: #fff; color: #294a60; font: inherit; font-weight: 850; cursor: pointer;`;
const EmptyState = styled.div`
  display: grid; justify-items: center; gap: .45rem; padding: 2.3rem 1.2rem; border: 1px dashed #cbd7df; border-radius: 1rem; background: #fff; color: #63737f; text-align: center;
  svg { color: #9a711f; font-size: 2.5rem; } h3 { margin: .25rem 0 0; color: #243d50; } p { max-width: 36rem; margin: 0; line-height: 1.6; }
`;
const ErrorMessage = styled.p`margin: 0 0 1rem; padding: .85rem; border: 1px solid #efcfcb; border-radius: .8rem; background: #fff6f5; color: #8c332c;`;

const mapStateToProps = ({ firebase, demands }) => ({
  artisanId: firebase?.auth?.uid || '',
  loading: Boolean(demands?.loading),
  error: demands?.error || demands?.deleteDemand?.error,
  userDemands: demands?.demands || [],
});
const mapDispatchToProps = (dispatch) => ({
  getDemands: () => dispatch(getDemandsForArtisanAPI()),
  savePortfolio: (payload) => dispatch(savePortfolioProject(payload)),
  confirmVisit: (demand) => dispatch(confirmVisitByArtisan(demand)),
  markAssessmentReady: (demand) => dispatch(markAssessmentReadyByArtisan(demand)),
  resolveCompletionIssue: (demand) => dispatch(resolveCompletionIssueArtisan(demand)),
});

export default connect(mapStateToProps, mapDispatchToProps)(MainJobSection);
